'use client';
import { Data, Operation, Variables } from '__generated__/graphql';
import { fetchClientOperation } from '$/modules/graphql-client/fetchClientOperation';

/**
 * This hook returns a function which wraps a gql operation. It allows to
 * use lazy query with trigger function instead of a regular hook that executes
 * immediately.
 */
function useLazyQuery<T extends Operation>(
  query: T,
): (args: Variables<T>) => Promise<Data<T> | undefined> {
  return async function triggerFunction(args) {
    try {
      const operation = fetchClientOperation(query, args);

      for await (const response of operation) {
        return response.data as Data<T>;
      }
    } catch (error) {
      throw new Error('Error occured in the trigger function');
    }
  };
}

function createLazyQuery<T extends Operation>(
  query: T,
): (args: Variables<T>) => Promise<Data<T> | undefined> {
  return async function triggerFunction(args) {
    try {
      const operation = fetchClientOperation(query, args);

      for await (const response of operation) {
        if (response.errors?.length) {
          console.error('GraphQL errors', response.errors);
        }
        return response.data as Data<T>;
      }
    } catch (error) {
      throw new Error('Error occured in the trigger function');
    }
  };
}

const query = (_: never) => {};
const EmptyQueryArgs = {} as Parameters<typeof query>[0];

export { useLazyQuery, createLazyQuery, EmptyQueryArgs };
